import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  searchPatientsAction,
  resetSearchPatientsAction,
} from 'src/redux/actions/documentBundleActions';
import { PatientSearchResult } from '../by-patient/types';

/** Below this, a search would return most of the facility and mean nothing. */
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

/**
 * SQ-160: resident search.
 *
 * Nothing fires until the user has typed. On page load the By-patient screen asks the
 * server for nothing at all — the old screen pulled 600+ patients before anyone had
 * touched it.
 */
export function usePatientSearch(query: string, limit = 25) {
  const dispatch = useDispatch();
  const { response, loading } = useSelector((state: any) => state.searchPatientsReducer);
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const trimmed = query.trim();
    const timer = setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debounced.length < MIN_QUERY_LENGTH) {
      dispatch(resetSearchPatientsAction());
      return;
    }
    dispatch(searchPatientsAction({ q: debounced, limit }));
  }, [debounced, limit, dispatch]);

  const results: PatientSearchResult[] = useMemo(() => response ?? [], [response]);

  return {
    results,
    loading,
    /** True once the user has typed enough for a search to have been worth running. */
    searched: debounced.length >= MIN_QUERY_LENGTH,
    minQueryLength: MIN_QUERY_LENGTH,
  };
}
